import React from 'react';
import { Typography } from 'antd';
import { TruncatedText } from '@spark';
import '@/app/styles/scrollbar.css';

const { Text } = Typography;

const HEADER_CELL_STYLE = {
  padding: '10px 12px',
  fontSize: 13,
  fontWeight: 600,
  color: '#6B7280',
  whiteSpace: 'nowrap',
};

const DATA_CELL_STYLE = {
  padding: '14px 12px',
  fontSize: 14,
  color: '#374151',
  whiteSpace: 'nowrap',
};

function ReviewPercentBar({ percent }) {
  const isComplete = percent >= 100;
  const fillColor = isComplete ? '#22C55E' : '#3B82F6';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'flex-end' }}>
      <div style={{ width: 80, height: 8, borderRadius: 4, background: '#E5E7EB', overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(percent, 100)}%`, height: '100%', borderRadius: 4, background: fillColor }} />
      </div>
      <span style={{ fontSize: 14, fontWeight: 400, color: fillColor, minWidth: 48, textAlign: 'right' }}>
        {percent.toFixed(1)}%
      </span>
    </div>
  );
}

export default function DistributionTable({ title, nameColumnHeader = 'Label Name', rows, flexGrow = false, showReview = true }) {
  if (!rows || rows.length === 0) return null;

  const grandTotal = rows.reduce((sum, r) => sum + (r.total ?? 0), 0);

  return (
    <div style={{ marginTop: 16, ...(flexGrow ? { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 } : {}) }}>
      <Text style={{ fontSize: 13, color: '#9CA3AF', display: 'block', marginBottom: 10 }}>
        {title}
      </Text>
      <div
        className="optune-scrollbar"
        style={{
          overflowY: 'auto', border: '1px solid #F3F4F6', borderRadius: 8,
          ...(flexGrow ? { flex: 1, minHeight: 0 } : { maxHeight: 400 }),
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#F9FAFB' }}>
              <th style={{ ...HEADER_CELL_STYLE, textAlign: 'left', paddingLeft: 20 }}>{nameColumnHeader}</th>
              <th style={{ ...HEADER_CELL_STYLE, textAlign: 'center' }}>Count</th>
              <th style={{ ...HEADER_CELL_STYLE, textAlign: 'center' }}>Distribution %</th>
              {showReview && (
                <>
                  <th style={{ ...HEADER_CELL_STYLE, textAlign: 'center' }}>Reviewed</th>
                  <th style={{ ...HEADER_CELL_STYLE, textAlign: 'right', paddingRight: 20 }}>Review %</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const total = row.total ?? 0;
              const validated = Math.max(Math.min(row.validated ?? 0, total), 0);
              const distPct = grandTotal > 0 ? (total / grandTotal) * 100 : 0;
              const reviewPct = total > 0 ? (validated / total) * 100 : 0;

              return (
                <tr key={row.label} style={{ borderBottom: '1px solid #F3F4F6' }}>
                  <td style={{ ...DATA_CELL_STYLE, textAlign: 'left', paddingLeft: 20, fontWeight: 500, color: '#111827' }}>
                    <TruncatedText text={row.label} maxWidth={180} style={{ fontSize: 14 }} />
                  </td>
                  <td style={{ ...DATA_CELL_STYLE, textAlign: 'center' }}>{total}</td>
                  <td style={{ ...DATA_CELL_STYLE, textAlign: 'center' }}>{distPct.toFixed(1)}%</td>
                  {showReview && (
                    <>
                      <td style={{ ...DATA_CELL_STYLE, textAlign: 'center' }}>{validated}/{total}</td>
                      <td style={{ ...DATA_CELL_STYLE, textAlign: 'right', paddingRight: 20 }}>
                        <ReviewPercentBar percent={reviewPct} />
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
